import { ErrorModal, useMesfRealtime } from "@dexteel/mesf-core";
import { Divider, Grid2, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SectionSelector } from "@/shared/selectors";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BilletChart } from "./components/BilletChart";
import { DelayAlert } from "./components/DelayAlert";
import { HeatChips } from "./components/HeatChips";
import { StandCard } from "./components/StandCard";
import { MillOverview } from "./models/MillOverview";
import { OverviewReponse } from "./models/OverviewResponse";
import { StandData } from "./models/StandData";
import { getMillOverview } from "./repositories/MillOverviewRepository";

type useMillOverviewsProps = {
	onError: (error: Error) => void;
};

export const useMillOverviews = ({ onError }: useMillOverviewsProps) => {
	return useQuery<OverviewReponse, Error>({
		queryKey: ["millOverviews"],
		queryFn: ({ signal }) => getMillOverview(signal),
		onError: onError,
		refetchInterval: 30_000,
		refetchOnWindowFocus: true,
	});
};

export const MillOverviewPage = () => {
	const [selectedRowData, setSelectedRowData] = useState<any>(null);
	const [error, setError] = useState("");
	const [campaignData, setCampaignData] = useState<MillOverview | null>(null);
	const [roughingStands, setRoughingStands] = useState<StandData[]>([]);
	const [finishingStands, setFinishingStands] = useState<StandData[]>([]);
	const [standsInstalleds, setStandsInstalleds] = useState<String[]>([]);

	const {
		data: overview,
		isLoading,
		isSuccess,
	} = useMillOverviews({
		onError: (error) => {
			setError(error.message);
		},
	});

	const queryClient = useQueryClient();

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") == "M2.GetMillOverview"
			) {
				await queryClient.invalidateQueries(["millOverviews"]);
			}
		},
	});

	useEffect(() => {
		if (isSuccess) {
			const standsData = overview?.tables[0]?.rows || [];
			const campaign = standsData.length > 0 ? standsData[0] : null;
			setCampaignData({
				CurrentCampaignId: campaign?.CurrentCampaignId,
				CurrentSectionId: campaign?.CurrentSectionId,
				CurrentSectionName: campaign?.CurrentSectionName,
				CurrentCampaignBillets: campaign?.CurrentCampaignBillets,
				CurrentCampaignWeight: campaign?.CurrentCampaignWeight,
			});
			setFinishingStands(
				standsData.filter((stand) => stand?.StandTypeCode == "F"),
			);
			setRoughingStands(
				standsData.filter((stand) => stand?.StandTypeCode == "R"),
			);

			let standsInstalleds: String[] = [];
			standsData.map((s) => {
				if (s.CarrierStatusCode === "I") {
					standsInstalleds.push(s.StandName);
				}
			});
			setStandsInstalleds(standsInstalleds);
		}
	}, [isSuccess, overview]);

	if (isLoading) {
		return <CenteredLazyLoading />;
	}

	return (
		<>
			<HelmetDexteel
				title={`Mill Overview${
					campaignData?.CurrentSectionName
						? ` - Section ${campaignData?.CurrentSectionName}`
						: ""
				}`}
			/>
			<Grid2
				container
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2
					container
					justifyContent="center"
					p={2}
					pb={5}
					spacing={1}
					alignContent="flex-start"
				>
					<Grid2
						spacing={{ xs: 2, md: 3, lg: 5 }}
						container
						justifyContent="space-between"
						alignItems="flex-start"
					>
						<Grid2 size={{ xs: 12, md: 6, lg: 4 }} display="flex">
							<Grid2
								container
								direction="column"
								justifyContent="flex-start"
								spacing={1}
								sx={{ flex: 1 }}
							>
								<Grid2>
									<Grid2
										container
										spacing={1}
										justifyContent="space-between"
										alignItems="center"
									>
										<Grid2>
											<Typography variant="h5" style={{ fontWeight: 600 }}>
												Mill Overview
											</Typography>
										</Grid2>
										<Grid2 container justifyContent="flex-end">
											<DelayAlert
												show={!!overview?.tables[1]?.rows[0]?.DelayId}
											/>
										</Grid2>
									</Grid2>
								</Grid2>

								<Grid2>
									<Grid2 container spacing={1} alignItems="center">
										<Grid2 size={{ xs: 12, sm: 4 }}>
											<SectionSelector
												label="Section"
												value={campaignData?.CurrentSectionId ?? null}
												disabled
											/>
										</Grid2>
										<Grid2 size={{ xs: 6, sm: 4 }}>
											<TextField
												label="Actual Tons"
												value={campaignData?.CurrentCampaignWeight?.toFixed(2)}
												variant="outlined"
												size="small"
												disabled
												fullWidth
											/>
										</Grid2>
										<Grid2 size={{ xs: 6, sm: 4 }}>
											<TextField
												label="Actual Billets"
												value={campaignData?.CurrentCampaignBillets}
												variant="outlined"
												size="small"
												disabled
												fullWidth
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						</Grid2>

						<Grid2 size={{ xs: 12, md: 6, lg: 4 }} display="flex">
							<Grid2
								container
								direction="column"
								justifyContent="flex-start"
								spacing={1.5}
								sx={{ flex: 1 }}
							>
								<Grid2>
									<Typography variant="h5" style={{ fontWeight: 600 }}>
										Production Schedule
									</Typography>
								</Grid2>
								<Grid2>
									<HeatChips setError={setError} />
								</Grid2>
							</Grid2>
						</Grid2>

						<Grid2
							size={{ xs: 12, md: 12, lg: 4 }}
							display="flex"
							justifyContent={{ xs: "center", lg: "flex-end" }}
							alignItems="stretch"
						>
							<BilletChart billetData={overview?.tables[2]?.rows || []} />
						</Grid2>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 12 }}>
						<Divider />
					</Grid2>

					<Grid2 container justifyContent="center">
						<Grid2
							container
							size={{ xs: 12 }}
							justifyContent="center"
							alignItems="stretch"
						>
							{roughingStands.map((stand) => (
								<StandCard
									stand={stand}
									selectedStand={selectedRowData}
									setSelectedStand={setSelectedRowData}
									standInstalleds={standsInstalleds}
									currentCampaignSectionId={
										campaignData?.CurrentSectionId ?? null
									}
								/>
							))}
						</Grid2>

						<Grid2
							container
							size={{ xs: 12 }}
							justifyContent="center"
							alignItems="stretch"
						>
							{finishingStands.map((stand) => (
								<StandCard
									stand={stand}
									selectedStand={selectedRowData}
									setSelectedStand={setSelectedRowData}
									standInstalleds={standsInstalleds}
									currentCampaignSectionId={
										campaignData?.CurrentSectionId ?? null
									}
								/>
							))}
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
